import React, { useState } from 'react';
import { EmailAuthProvider, linkWithCredential, getAuth } from 'firebase/auth';
import { useAuth } from '../../hooks/useAuth';
import { Card, Input, Alert, Spinner, Button } from '../ui';
import Logger from '../../utils/logger';

interface LinkEmailPasswordProps {
  onSuccess?: () => void;
}

const logger = Logger.create('LinkEmailPassword');

export const LinkEmailPassword: React.FC<LinkEmailPasswordProps> = ({
  onSuccess,
}) => {
  const { currentUser } = useAuth();
  const [email, setEmail] = useState(currentUser?.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleLinkEmailPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser) {
      setError('No user signed in');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create email/password credential
      const credential = EmailAuthProvider.credential(email, password);

      // Link it to the current user
      const auth = getAuth();
      if (!auth.currentUser) {
        throw new Error('No Firebase user available');
      }
      await linkWithCredential(auth.currentUser, credential);

      setSuccess(true);
      logger.debug('Email/password successfully linked to account');

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      logger.error('Error linking email/password:', error);

      const errorObj =
        error && typeof error === 'object'
          ? (error as Record<string, unknown>)
          : {};
      const code = String(errorObj.code || '');
      const message = String(errorObj.message || '');

      if (code === 'auth/credential-already-in-use') {
        setError('This email is already associated with another account.');
      } else if (code === 'auth/email-already-in-use') {
        setError('This email is already in use by another account.');
      } else if (code === 'auth/provider-already-linked') {
        setError('Email/password is already linked to this account.');
      } else {
        setError(message || 'Failed to link email/password');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Card>
        <Alert variant="success">
          <h3 className="font-semibold mb-1">
            Email/Password Successfully Linked!
          </h3>
          <p className="text-sm">
            You can now sign in using either Google or your email/password.
          </p>
        </Alert>
      </Card>
    );
  }

  return (
    <Card>
      <h2 className="text-lg font-semibold text-secondary-900 mb-2">
        Link Email/Password to Your Account
      </h2>
      <p className="text-sm text-secondary-600 mb-4">
        Add email/password sign-in to your existing Google account.
      </p>

      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      <form onSubmit={handleLinkEmailPassword}>
        <div className="space-y-4">
          <Input
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={!!currentUser?.email}
            helperText={
              currentUser?.email ? 'Using your current email address' : ''
            }
            fullWidth
          />

          <Input
            label="New Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            helperText="Minimum 6 characters"
            fullWidth
          />

          <Input
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            fullWidth
          />
        </div>

        <Button
          type="submit"
          variant="primary"
          disabled={loading || !password || !confirmPassword}
          className="w-full mt-6"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <Spinner size="sm" />
              Linking...
            </span>
          ) : (
            'Link Email/Password'
          )}
        </Button>
      </form>
    </Card>
  );
};
